import { Head, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { AnalyticsEmptyState } from "@/components/admin/analytics/analytics-empty-state"
import { AnalyticsErrorState } from "@/components/admin/analytics/analytics-error-state"
import { AnalyticsGrowthChart } from "@/components/admin/analytics/analytics-growth-chart"
import { AnalyticsKpiStrip } from "@/components/admin/analytics/analytics-kpi-strip"
import { AnalyticsOverlapCard } from "@/components/admin/analytics/analytics-overlap-card"
import { AnalyticsTopTenantsTable } from "@/components/admin/analytics/analytics-top-tenants-table"
import { AnalyticsWindowToggle } from "@/components/admin/analytics/analytics-window-toggle"
import AppLayout from "@/layouts/app-layout"
import { adminAnalyticsPath } from "@/routes"
import {
  type AdminAnalyticsProps,
  type AnalyticsWindow,
  DEFAULT_WINDOW,
} from "@/types/admin-analytics"

/**
 * OKS-66 — Admin Platform Analytics page (`/admin/analytics`).
 *
 * # State branch (TRD §4)
 *
 *   1. `state === "error"`   → `<AnalyticsErrorState>` with Retry.
 *   2. `state === "empty"`   → `<AnalyticsEmptyState>` (full-page).
 *   3. `state === "success"` → window toggle + KPI strip + overlap
 *                              card + growth chart + top-5 table.
 *
 * # Partial reload (TRD K11 — LQE-locked)
 *
 * Window toggle change → `router.get(adminAnalyticsPath({window}),
 * {}, {only: ['kpis', 'growth'], preserveScroll: true})`. Overlap,
 * top tenants, and the state discriminator never re-fetch on window
 * change. (UX Spec §1's `only: ['kpis_window', 'growth']` is the
 * documented erratum — the flat-KPI shape means `kpis_window`
 * doesn't exist as a discrete prop.)
 *
 * # Brand isolation NOTE
 *
 * This surface aggregates across all tenants by design — admin staff
 * see brand names directly. PRD §6 + CLAUDE.md exception; LQE QA Plan
 * NI1–NI3 cover the inverted-isolation assertion (admin DOES see all).
 *
 * # No `localStorage` for window
 *
 * URL query string only (CLAUDE.md hard rule). The server's `kpis.
 * active_brands_window` field is the source of truth for the active
 * tab — never URL-derived (off-whitelist URLs are coerced server-side
 * to `30d`, and we want the tab to reflect what the data actually is).
 */
export default function AdminAnalytics({
  state,
  kpis,
  overlap,
  growth,
  top_tenants: topTenants,
}: AdminAnalyticsProps) {
  const { t } = useTranslation()

  // Window comes from the server (already coerced + whitelisted). Fall
  // back to the default on error/empty branches where kpis is null —
  // the toggle still renders so admin can pivot off the error/empty
  // state without leaving the page.
  const window: AnalyticsWindow = kpis?.active_brands_window ?? DEFAULT_WINDOW

  return (
    <AppLayout>
      <Head title={t("admin.analytics.head_title")} />
      <div
        className="flex flex-col gap-6 p-4 md:p-6"
        data-testid="admin-analytics-page"
        role="region"
        aria-label={t("admin.analytics.heading")}
      >
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("admin.analytics.heading")}
          </h1>
          {state !== "empty" && (
            <AnalyticsWindowToggle
              value={window}
              onChange={(next) => {
                router.get(
                  adminAnalyticsPath({ window: next }),
                  {},
                  {
                    only: ["kpis", "growth"],
                    preserveScroll: true,
                    preserveState: false,
                  },
                )
              }}
            />
          )}
        </header>

        {state === "error" ? (
          <AnalyticsErrorState />
        ) : state === "empty" ? (
          <AnalyticsEmptyState />
        ) : (
          <>
            {kpis && <AnalyticsKpiStrip kpis={kpis} />}
            {overlap && <AnalyticsOverlapCard overlap={overlap} />}
            {growth && <AnalyticsGrowthChart growth={growth} />}
            {topTenants && <AnalyticsTopTenantsTable tenants={topTenants} />}
          </>
        )}
      </div>
    </AppLayout>
  )
}
